/**
 * Cookie-consent state, GDPR-style:
 * - 'all'       → necessary + anonymous usage analytics
 * - 'essential' → necessary only (language cookie, sign-in session)
 * - null        → no choice yet → the banner is shown and analytics stay OFF
 *
 * The choice lives in localStorage (client logic) and a cookie (available to
 * the server if ever needed). Nothing besides first-party analytics is gated
 * today — we set no advertising or third-party cookies at all.
 */

export type ConsentChoice = 'all' | 'essential';

const STORAGE_KEY = 'vn-consent';
const COOKIE_NAME = 'vn-consent';
const MAX_AGE_DAYS = 180; // choice expires and is re-asked twice a year

export function getConsent(): ConsentChoice | null {
  if (typeof window === 'undefined') return null;
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'all' || value === 'essential' ? value : null;
  } catch {
    return null;
  }
}

export function setConsent(choice: ConsentChoice): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    /* private mode — cookie below still records it */
  }
  document.cookie = `${COOKIE_NAME}=${choice};path=/;max-age=${MAX_AGE_DAYS * 86_400};samesite=lax`;
  window.dispatchEvent(new CustomEvent('vn-consent-change', { detail: choice }));
}

/** Whether anonymous usage analytics may run. */
export function analyticsAllowed(): boolean {
  return getConsent() === 'all';
}
